import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PartyPopper, Check, Loader2, Sparkles, CalendarCheck } from "lucide-react";
import { DunkinAddons } from "../components/DunkinAddons";

export default function PartyDeposit() {
  const { id } = useParams();
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [dunkin, setDunkin] = useState([]);
  const [noUpgrades, setNoUpgrades] = useState(false);
  const [chosenDate, setChosenDate] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/parties/deposit-page/${id}`)
      .then(({ data }) => {
        setParty(data);
        setSelected(data.selected_addons || []);
        setDunkin(data.selected_dunkin || []);
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "This party could not be found."))
      .finally(() => setLoading(false));
  }, [id]);

  const toggleAddon = (key) => {
    setNoUpgrades(false);
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const chooseNoUpgrades = () => {
    setNoUpgrades(true);
    setSelected([]);
  };

  const proceed = async () => {
    if (needsDateChoice && chosenDate == null) {
      toast.error("Please choose your party date first.");
      return;
    }
    setBusy(true);
    try {
      const opt = needsDateChoice ? party.date_options[chosenDate] : null;
      const { data } = await api.post(`/parties/deposit-page/${id}/addons`, {
        addons: selected, no_upgrades: noUpgrades, dunkin_addons: dunkin,
        chosen_date: opt?.date || null, chosen_time: opt?.time || null,
      });
      window.location.href = data.url; // Stripe deposit checkout ($100 only)
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen"><Loader2 className="mx-auto animate-spin text-[#E0218A]" size={40} /></div>;
  }
  if (error) {
    return (
      <div data-testid="party-deposit-error" className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen px-6">
        <h1 className="font-serif font-black text-4xl text-[#121212]">Hmm, something's off</h1>
        <p className="mt-4 text-[#121212]/70">{error}</p>
      </div>
    );
  }
  if (party.deposit_paid) {
    return (
      <div data-testid="party-deposit-paid" className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen px-6">
        <PartyPopper className="mx-auto text-[#E0218A]" size={52} />
        <h1 className="font-serif font-black text-5xl text-[#121212] mt-6">You're all set!</h1>
        <p className="mt-4 text-[#121212]/80 text-lg max-w-xl mx-auto">Your deposit is already paid and your party is confirmed. We'll be in touch with the final details before your date.</p>
      </div>
    );
  }

  const amount = Number(party.deposit_amount || 0);
  const dateLine = [party.desired_date, party.preferred_time].filter(Boolean).join(" · ");
  const needsDateChoice = (party.date_options || []).length > 0;

  return (
    <div data-testid="party-deposit-page" className="fade-in bg-[#F2E9D8] min-h-screen pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-6 sm:px-10">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#E0218A]" />
          <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#E0218A]">Reserve your party</span>
        </div>
        <h1 className="font-serif font-black text-[#121212] leading-[0.95] text-5xl sm:text-6xl">Almost there, {party.name?.split(" ")[0] || "friend"}!</h1>

        {/* availability confirmed summary — or date picker when alternates are offered */}
        {needsDateChoice ? (
          <div data-testid="party-date-picker" className="mt-8 bg-white border-2 border-[#121212] p-6">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/60 inline-flex items-center gap-2"><CalendarCheck size={15} className="text-[#E0218A]" /> Choose your party date</p>
            <p className="font-serif font-black text-2xl text-[#121212] mt-2">{party.package || "Your party"}</p>
            <p className="text-[#121212]/70 text-sm mt-1">Your first-choice date wasn't available — please pick one of these open dates for your party:</p>
            <div className="mt-4 space-y-3">
              {party.date_options.map((o, i) => {
                const on = chosenDate === i;
                return (
                  <label key={i} data-testid={`date-option-${i}`}
                    className={`flex items-center gap-4 p-4 border-2 cursor-pointer transition-all ${on ? "border-[#E0218A] bg-[#E0218A]/5" : "border-[#121212] bg-white hover:bg-white/60"}`}>
                    <span className={`shrink-0 w-6 h-6 rounded-full border-2 border-[#121212] flex items-center justify-center ${on ? "bg-[#E0218A]" : "bg-white"}`}>
                      {on && <Check size={14} className="text-white" strokeWidth={3} />}
                    </span>
                    <input type="radio" name="party-date" className="sr-only" checked={on} onChange={() => setChosenDate(i)} />
                    <span className="font-bold text-[#121212]">{o.date}{o.time ? ` · ${o.time}` : ""}</span>
                  </label>
                );
              })}
            </div>
            {party.num_guests && <p className="text-[#121212]/60 text-sm mt-3">{party.num_guests} decorating guests</p>}
          </div>
        ) : (
          <div data-testid="party-summary" className="mt-8 bg-white border-2 border-[#121212] p-6">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/60 inline-flex items-center gap-2"><CalendarCheck size={15} className="text-[#C6FF33]" /> Date confirmed &amp; available</p>
            <p className="font-serif font-black text-2xl text-[#121212] mt-2">{party.package || "Your party"}</p>
            {dateLine && <p className="text-[#121212]/80 mt-1">{dateLine}</p>}
            {party.num_guests && <p className="text-[#121212]/60 text-sm mt-1">{party.num_guests} decorating guests</p>}
          </div>
        )}

        {/* add-ons */}
        <div className="mt-8">
          <h2 className="font-serif font-black text-3xl text-[#121212] flex items-center gap-3"><Sparkles size={24} className="text-[#E0218A]" fill="#E0218A" /> Make it extra special</h2>
          <p className="mt-3 text-[#121212]/75 leading-relaxed">Choose any optional upgrades you would like to include. These selections will not be charged today and will be added to your final party balance.</p>

          <div className="mt-6 space-y-4" data-testid="party-addons">
            {(party.addons_catalog || []).map((a) => {
              const checked = selected.includes(a.key);
              return (
                <label
                  key={a.key}
                  data-testid={`addon-${a.key}`}
                  className={`flex gap-4 p-5 border-2 cursor-pointer transition-all ${checked ? "border-[#E0218A] bg-[#E0218A]/5" : "border-[#121212] bg-white hover:bg-white/60"}`}
                >
                  <span className={`shrink-0 mt-0.5 w-6 h-6 border-2 border-[#121212] flex items-center justify-center ${checked ? "bg-[#E0218A]" : "bg-white"}`}>
                    {checked && <Check size={16} className="text-white" strokeWidth={3} />}
                  </span>
                  <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleAddon(a.key)} />
                  <span className="flex-1">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="font-bold text-[#121212]">{a.label}</span>
                      <span className="font-serif font-black text-[#121212] whitespace-nowrap">{a.price_label}</span>
                    </span>
                    <span className="block text-sm text-[#121212]/70 mt-1 leading-relaxed">{a.desc}</span>
                  </span>
                </label>
              );
            })}

            <label
              data-testid="addon-none"
              className={`flex items-center gap-4 p-5 border-2 cursor-pointer transition-all ${noUpgrades ? "border-[#121212] bg-[#121212]/5" : "border-stone-300 bg-white hover:border-[#121212]"}`}
            >
              <span className={`shrink-0 w-6 h-6 border-2 border-[#121212] flex items-center justify-center ${noUpgrades ? "bg-[#121212]" : "bg-white"}`}>
                {noUpgrades && <Check size={16} className="text-white" strokeWidth={3} />}
              </span>
              <input type="checkbox" className="sr-only" checked={noUpgrades} onChange={chooseNoUpgrades} />
              <span className="font-bold text-[#121212]">No upgrades at this time</span>
            </label>
          </div>

          <p className="mt-4 text-xs text-[#121212]/60 italic">Favor bag quantity is finalized once your decorating guest count is confirmed the Monday before your party. You can add, remove, or adjust upgrades any time until then.</p>
        </div>

        {/* Dunkin' catering */}
        {party.dunkin_catalog?.length > 0 && (
          <div className="mt-8">
            <DunkinAddons catalog={party.dunkin_catalog} value={dunkin} onChange={setDunkin} />
          </div>
        )}

        {/* deposit */}
        <div className="mt-8 bg-white border-2 border-[#121212] p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/60">Due today</p>
            <p data-testid="deposit-amount" className="font-serif font-black text-3xl text-[#121212]">${amount.toFixed(2)}</p>
          </div>
          <p className="text-sm text-[#121212]/70 mt-2">Only the ${amount.toFixed(2)} deposit is charged today. Any add-ons you selected will be added to your final party balance — not charged now.</p>
          <button
            data-testid="pay-deposit-btn"
            onClick={proceed}
            disabled={busy}
            className="mt-5 w-full px-8 py-4 bg-[#E0218A] text-white hover:bg-[#c41879] disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all inline-flex items-center justify-center gap-3"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : `Pay $${amount.toFixed(2)} Deposit`}
          </button>
        </div>
      </div>
    </div>
  );
}
